import { create } from 'zustand'
import { useAuthStore } from '@/shared/stores/auth.store'
import type { SearchTab } from '../hooks/use-global-search'
import type { ChartIdOperator, ChartIdSearchRequest, IdField } from '../hooks/use-chart-id-search'
import type { CommaAs } from '../utils/parse-identifiers'
import type { ChartBucketKey } from '@/features/workspace/utils/status-map'

interface SearchFormState {
  /** The header search's dropdown is open. */
  open: boolean
  /** What is in the panel's one textarea: a single query or a pasted list. */
  text: string
  /** Comma-only text the user chose to "Search as N IDs" (null = auto-detect). */
  commaAs: CommaAs
  /** The panel's category tab. */
  tab: SearchTab
  /** Bulk mode: chosen identifier field; null = the first field the options allow. */
  field: IdField | null
  /** List-search match operator (bulk only); null = Exact. */
  match: ChartIdOperator | null
  facilityIds: string[]
  /** Status filter: Chart Queue / History tab keys (CHART_BUCKET_LABELS). */
  buckets: ChartBucketKey[]
  /**
   * The submitted chart-ID search (including its page) — what the results query
   * runs for a pasted list. Cleared when the text stops being a list
   * (search-panel `setText`).
   */
  request: ChartIdSearchRequest | null
  /** The panel shows the bulk results (false = the ID editor). */
  showBulk: boolean
}

interface SearchState extends SearchFormState {
  patch: (p: Partial<SearchFormState>) => void
  reset: () => void
}

const INITIAL: SearchFormState = {
  open: false,
  text: '',
  commaAs: null,
  tab: 'all',
  field: null,
  match: null,
  facilityIds: [],
  buckets: [],
  request: null,
  showBulk: false,
}

/**
 * The header search panel's text, filters and submitted bulk request (its
 * results, with their page, live in the panel only).
 *
 * MEMORY ONLY, deliberately: the text is claim ids / Patient IDs, so no
 * `persist`, no URL params, no browser storage. A reload starts empty. Cleared
 * below whenever the signed-in identity or the active client changes.
 */
export const useSearchStore = create<SearchState>((set) => ({
  ...INITIAL,
  patch: (p) => set(p),
  reset: () => set(INITIAL),
}))

// Logout (user → null), a different login, impersonation (user id changes) or a
// client switch (auth user's active_client — what the client switcher writes)
// must never show the previous identity's identifiers. Guarded so the
// module-load side effect no-ops when a test mocks the store as a bare hook
// (same pattern as client-admin/stores/dashboard-filters.store.ts).
if (typeof useAuthStore.subscribe === 'function') {
  useAuthStore.subscribe((state, prev) => {
    if (
      state.user?.id !== prev.user?.id
      || state.user?.active_client?.id !== prev.user?.active_client?.id
      || !!state.user?.is_impersonating !== !!prev.user?.is_impersonating
    ) {
      useSearchStore.getState().reset()
    }
  })
}
